import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { SelectField } from '@wk/components-v3-react16';
import { useFileSizeFilter } from './useFileSizeFilter';
import type { FileSizeFilterModel, FilterType } from './types';
import styles from './FileSizeFilter.module.scss';

interface FileSizeFilterProperties
  extends CustomFilterDisplayProps<FileSizeFilterModel>,
    FilterWrapperParams {}

const parseValueFromModel = (value?: number): string => {
  return value === undefined ? '' : value.toString();
};

const getPrimaryFromModel = (model?: FileSizeFilterModel | null): string => {
  if (!model) {
    return '';
  }
  return model.type === 'lessThan'
    ? parseValueFromModel(model.fileSizeLess)
    : parseValueFromModel(model.fileSizeMore);
};

const getSecondaryFromModel = (model?: FileSizeFilterModel | null): string => {
  return model?.type === 'inRange'
    ? parseValueFromModel(model.fileSizeLess)
    : '';
};

const FileSizeFilter = ({
  model,
  onStateChange,
  state,
}: FileSizeFilterProperties) => {
  const componentReference = useRef<HTMLDivElement>(null);
  const initialModelSet = useRef(false);

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        componentReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({ afterGuiAttached });
  useFileSizeFilter(model);

  const defaultFilterType: FilterType = 'greaterThan';

  const [filterType, setFilterType] = useState<FilterType>(
    state.model?.type || defaultFilterType
  );
  const [primaryValue, setPrimaryValue] = useState<string>(
    getPrimaryFromModel(state.model)
  );
  const [secondaryValue, setSecondaryValue] = useState<string>(
    getSecondaryFromModel(state.model)
  );

  // Set initial model when filter opens (so Apply works even without interaction)
  useEffect(() => {
    if (!state.model && !initialModelSet.current) {
      const initialModel = buildModel(filterType, primaryValue, secondaryValue);
      if (initialModel) {
        onStateChange({ model: initialModel });
        initialModelSet.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync state when model changes (for Reset button)
  useEffect(() => {
    if (state.model) {
      setFilterType(state.model.type);
      setPrimaryValue(getPrimaryFromModel(state.model));
      setSecondaryValue(getSecondaryFromModel(state.model));
    } else {
      setPrimaryValue('');
      setSecondaryValue('');
    }
  }, [state.model]);

  const filterTypeOptions = useMemo(
    () => [
      { label: 'Greater Than', value: 'greaterThan' },
      { label: 'Less Than', value: 'lessThan' },
      { label: 'Between', value: 'inRange' },
    ],
    []
  );

  const buildModel = useCallback(
    (
      type: FilterType,
      primaryString: string,
      secondaryString: string
    ): FileSizeFilterModel | null => {
      const primaryNumber = Number.parseFloat(primaryString);
      const secondaryNumber = Number.parseFloat(secondaryString);
      const hasPrimary = !Number.isNaN(primaryNumber) && primaryNumber >= 0;
      const hasSecondary =
        !Number.isNaN(secondaryNumber) && secondaryNumber >= 0;

      if (type === 'greaterThan') {
        return hasPrimary ? { fileSizeMore: primaryNumber, type } : null;
      }
      if (type === 'lessThan') {
        return hasPrimary ? { fileSizeLess: primaryNumber, type } : null;
      }
      // inRange
      if (!hasPrimary && !hasSecondary) {
        return null;
      }
      const newModel: FileSizeFilterModel = { type };
      if (hasPrimary) {
        newModel.fileSizeMore = primaryNumber;
      }
      if (hasSecondary) {
        newModel.fileSizeLess = secondaryNumber;
      }
      return newModel;
    },
    []
  );

  const handleFilterTypeChange = (newType: FilterType) => {
    setFilterType(newType);
    onStateChange({
      model: buildModel(newType, primaryValue, secondaryValue),
    });
  };

  const handlePrimaryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
        return;
      }

      setPrimaryValue(value);

      onStateChange({ model: buildModel(filterType, value, secondaryValue) });
    },
    [filterType, secondaryValue, buildModel, onStateChange]
  );

  const handleSecondaryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
        return;
      }

      setSecondaryValue(value);

      onStateChange({ model: buildModel(filterType, primaryValue, value) });
    },
    [filterType, primaryValue, buildModel, onStateChange]
  );

  return (
    <div className={styles.container} ref={componentReference} tabIndex={-1}>
      <div className={styles.filterContent}>
        <div className={styles.filterRow}>
          <div className={styles.filterSelect}>
            <SelectField size="medium">
              <select
                value={filterType}
                onChange={(event) =>
                  handleFilterTypeChange(event.target.value as FilterType)
                }
              >
                {filterTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </SelectField>
          </div>
        </div>

        {/* Primary input — always shown; 'from' value for inRange */}
        <div className={styles.inputSection}>
          <div className={styles.inputRow}>
            <div className={styles.megabytesInput}>
              <input
                type="text"
                value={primaryValue}
                onChange={handlePrimaryChange}
                placeholder={
                  filterType === 'inRange'
                    ? 'Enter from # bytes'
                    : 'Enter # bytes'
                }
              />
            </div>
          </div>
        </div>

        {/* Secondary input — 'to' value, shown only for inRange */}
        {filterType === 'inRange' && (
          <div className={styles.inputSection}>
            <div className={styles.inputRow}>
              <div className={styles.megabytesInput}>
                <input
                  type="text"
                  value={secondaryValue}
                  onChange={handleSecondaryChange}
                  placeholder="Enter to # bytes"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileSizeFilter;
